import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useOrg } from '../../components/OrgLayout'
import { useOrgApps } from '../../hooks/useOrgApps'
import { useOrgPermissions } from '../../hooks/useOrgPermissions'
import {
  VStack,
  HStack,
  Card,
  Text,
  Badge,
  Switch,
  Avatar,
  Skeleton,
  Banner,
  PageHeader,
  Animate,
  EmptyState
} from 'wss3-forge'
import {
  Apps20Regular,
  Key20Regular,
  Checkmark20Regular,
  Dismiss20Regular,
  ArrowRight20Regular,
  People20Regular,
  LockClosed20Regular,
  ChevronRight20Regular
} from '@fluentui/react-icons'

export function OrgApps() {
  const navigate = useNavigate()
  const { organization } = useOrg()
  const { apps, loading, error, loadApps, toggleApp } = useOrgApps()
  const { loadPermissions, canManageApps } = useOrgPermissions()
  const [toggling, setToggling] = useState<string | null>(null)

  useEffect(() => {
    loadApps(organization.id)
    loadPermissions(organization.id)
  }, [organization.id, loadApps, loadPermissions])

  const handleToggle = async (appId: string, enabled: boolean) => {
    setToggling(appId)
    await toggleApp(organization.id, appId, enabled)
    setToggling(null)
  }

  if (loading) {
    return (
      <VStack gap="lg">
        <Skeleton width={200} height={32} />
        <VStack gap="md">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} width="100%" height={100} />
          ))}
        </VStack>
      </VStack>
    )
  }

  return (
    <VStack gap="lg">
      <Animate type="fadeIn">
        <PageHeader
          title="Apps & Services"
          subtitle="Enable or disable Webba applications for your organization."
        />
      </Animate>

      {error && (
        <Banner variant="error" title="Error">
          {error}
        </Banner>
      )}

      {apps.length === 0 ? (
        <Animate type="fadeIn" delay={100}>
          <EmptyState
            icon={<Apps20Regular />}
            title="No applications available"
            description="There are no Webba applications available at this time."
          />
        </Animate>
      ) : (
        <Animate type="slideInUp" delay={50}>
          <VStack gap="md">
            {apps.map((app, index) => (
              <Animate key={app.app_id} type="slideInUp" delay={50 + index * 30}>
                <Card
                  padding="lg"
                  style={{ cursor: 'pointer', transition: 'box-shadow 0.2s ease' }}
                  onClick={() => navigate(`/org/${organization.slug}/apps/${app.app_id}`)}
                >
                  <HStack gap="lg" style={{ alignItems: 'center' }}>
                    <Avatar
                      name={app.app_name}
                      src={app.app_image_url || undefined}
                      size="lg"
                    />

                    <VStack gap="xs" style={{ flex: 1 }}>
                      <HStack gap="sm" style={{ alignItems: 'center' }}>
                        <Text weight="semibold" size="lg">{app.app_name}</Text>
                        {app.is_mandatory && (
                          <Badge size="sm" variant="primary">
                            <HStack gap="xs" style={{ alignItems: 'center' }}>
                              <LockClosed20Regular style={{ fontSize: '12px' }} />
                              <span>Required</span>
                            </HStack>
                          </Badge>
                        )}
                        {app.has_sso_connection && (
                          <Badge size="sm" variant="success">
                            <HStack gap="xs" style={{ alignItems: 'center' }}>
                              <Key20Regular style={{ fontSize: '12px' }} />
                              <span>SSO</span>
                            </HStack>
                          </Badge>
                        )}
                      </HStack>
                      {app.app_description && (
                        <Text size="sm" color="muted">{app.app_description}</Text>
                      )}
                      {app.is_enabled && (
                        <HStack gap="lg">
                          <Text size="sm" color="muted">
                            {app.logins_30d} logins (30d)
                          </Text>
                          {app.active_subscriptions > 0 && (
                            <HStack gap="xs" style={{ alignItems: 'center' }}>
                              <People20Regular style={{ fontSize: '14px', color: 'var(--brand-primary)' }} />
                              <Text size="sm" color="muted">
                                {app.active_subscriptions} active subscription{app.active_subscriptions !== 1 ? 's' : ''}
                              </Text>
                            </HStack>
                          )}
                          {app.last_login_at && (
                            <Text size="sm" color="muted">
                              Last: {new Date(app.last_login_at).toLocaleDateString('en-US')}
                            </Text>
                          )}
                        </HStack>
                      )}
                      {app.is_enabled && app.subscription_plans && app.subscription_plans.length > 0 && (
                        <HStack gap="sm" style={{ marginTop: '4px' }}>
                          {app.subscription_plans.map((p, idx) => (
                            <Badge key={idx} size="sm" variant="default">
                              {p.plan}: {p.count}
                            </Badge>
                          ))}
                        </HStack>
                      )}
                    </VStack>

                    <HStack gap="md" style={{ alignItems: 'center' }}>
                      {app.is_enabled && app.has_sso_connection && (
                        <div onClick={(e) => {
                          e.stopPropagation()
                          navigate(`/org/${organization.slug}/sso`)
                        }}>
                          <Badge
                            size="sm"
                            variant="default"
                            style={{ cursor: 'pointer' }}
                          >
                            <HStack gap="xs" style={{ alignItems: 'center' }}>
                              <span>Configure SSO</span>
                              <ArrowRight20Regular style={{ fontSize: '12px' }} />
                            </HStack>
                          </Badge>
                        </div>
                      )}

                      {app.is_enabled ? (
                        <Badge size="sm" variant="success">
                          <HStack gap="xs" style={{ alignItems: 'center' }}>
                            <Checkmark20Regular style={{ fontSize: '12px' }} />
                            <span>Enabled</span>
                          </HStack>
                        </Badge>
                      ) : (
                        <Badge size="sm" variant="default">
                          <HStack gap="xs" style={{ alignItems: 'center' }}>
                            <Dismiss20Regular style={{ fontSize: '12px' }} />
                            <span>Disabled</span>
                          </HStack>
                        </Badge>
                      )}

                      <div onClick={(e) => e.stopPropagation()}>
                        <Switch
                          checked={app.is_enabled}
                          onChange={(checked) => handleToggle(app.app_id, checked)}
                          disabled={!canManageApps || toggling === app.app_id || app.is_mandatory}
                        />
                      </div>

                      <ChevronRight20Regular style={{ color: 'var(--text-muted)' }} />
                    </HStack>
                  </HStack>
                </Card>
              </Animate>
            ))}
          </VStack>
        </Animate>
      )}

      <Animate type="fadeIn" delay={200}>
        <Card padding="md" variant="outlined">
          <Text size="sm" color="muted">
            Enabling an app allows your organization members to access it via SSO.
            Configure SSO connections in the SSO Connections page to complete the setup.
          </Text>
        </Card>
      </Animate>
    </VStack>
  )
}
